import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Plus, AlertTriangle } from "lucide-react";
import { api } from "@/lib/api";
import { MONTHS_SHORT, WEEK_LETTERS, longDate, monthGrid, toISO, todayISO, useOccurrences } from "@/lib/calendar";
import { Legend, OccChip, useCalendarDialogs } from "@/components/calendar/parts";

const MonthChips = ({ year, month, onPick }) => {
  const ref = useRef(null);
  const list = Array.from({ length: 25 }, (_, i) => new Date(year, month - 12 + i, 1));
  useEffect(() => { ref.current?.querySelector("[data-active='1']")?.scrollIntoView({ inline: "center", block: "nearest" }); }, [year, month]);
  return (
    <div ref={ref} className="flex items-center gap-2 overflow-x-auto no-scrollbar py-1" data-testid="cal-month-chips">
      {list.map((m, i) => {
        const active = m.getMonth() === month && m.getFullYear() === year;
        const showYear = m.getMonth() === 0;
        return (
          <React.Fragment key={i}>
            {showYear && <span className="text-sm font-semibold text-slate-600 shrink-0">{m.getFullYear()}</span>}
            <button onClick={() => onPick(m)} data-active={active ? "1" : "0"} data-testid={`cal-month-${m.getFullYear()}-${m.getMonth() + 1}`}
              className={`shrink-0 px-4 h-9 rounded-full border text-sm transition-colors ${active ? "bg-[#d3e3fd] border-[#d3e3fd] text-[#0b57d0] font-semibold" : "border-slate-400 text-slate-700 hover:bg-slate-50"}`}>
              {MONTHS_SHORT[m.getMonth()]}
            </button>
          </React.Fragment>
        );
      })}
    </div>
  );
};

const MonthGrid = ({ cells, month, byDay, selected, onSelect, onOpen }) => (
  <div className="grid grid-cols-7 border-t border-l border-slate-200" data-testid="cal-month-grid">
    {WEEK_LETTERS.map((w, i) => <div key={i} className="text-center text-[11px] text-slate-500 py-1.5 border-r border-slate-200">{w}</div>)}
    {cells.map((c) => {
      const iso = toISO(c);
      const list = byDay[iso] || [];
      const isSel = iso === selected;
      const isToday = iso === todayISO();
      return (
        <div key={iso} onClick={() => onSelect(iso)} role="button" data-testid={`cal-cell-${iso}`}
          className={`min-h-[78px] md:min-h-[104px] border-r border-b border-slate-200 p-0.5 cursor-pointer transition-colors ${isSel ? "bg-blue-50" : "hover:bg-slate-50"}`}>
          <p className={`text-center text-[12px] mb-0.5 mx-auto w-6 h-6 leading-6 rounded-full ${isToday ? "bg-[#1a73e8] text-white font-bold" : c.getMonth() === month ? "text-slate-800" : "text-slate-400"}`}>{c.getDate()}</p>
          <div className="space-y-px">
            {list.slice(0, 3).map((o) => <OccChip key={o.item_id} o={o} onClick={onOpen} size="sm" />)}
            {list.length > 3 && <p className="text-[9.5px] text-slate-500 px-1">+{list.length - 3}</p>}
          </div>
        </div>
      );
    })}
  </div>
);

export default function Calendario() {
  const [params] = useSearchParams();
  const overdueRef = useRef(null);
  const now = new Date();
  const [ym, setYm] = useState({ y: now.getFullYear(), m: now.getMonth() });
  const [selected, setSelected] = useState(todayISO());
  const [overdue, setOverdue] = useState([]);
  const cells = useMemo(() => monthGrid(ym.y, ym.m), [ym]);
  const { items, reload } = useOccurrences(toISO(cells[0]), toISO(cells[41]));
  const loadOverdue = () => api.get("/calendar/overdue").then((r) => setOverdue(r.data)).catch(() => {});
  useEffect(() => { loadOverdue(); }, []);
  const refresh = () => { reload(); loadOverdue(); };
  const { openOcc, openNew, dialogs } = useCalendarDialogs(refresh);
  const byDay = useMemo(() => items.reduce((a, o) => ((a[o.date] = a[o.date] || []).push(o), a), {}), [items]);
  const dayList = byDay[selected] || [];
  const showOverdue = overdue.length > 0;
  useEffect(() => {
    if (showOverdue && params.get("atrasados") === "1") overdueRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [showOverdue, params]);

  return (
    <div className="space-y-3 pb-20" data-testid="calendario-page">
      <div className="flex items-baseline gap-2">
        <h1 className="font-heading text-2xl font-black text-slate-900 capitalize">{new Date(ym.y, ym.m, 1).toLocaleDateString("pt-BR", { month: "long" })}</h1>
        <span className="text-slate-500 text-sm">{ym.y}</span>
        <button onClick={() => { setYm({ y: now.getFullYear(), m: now.getMonth() }); setSelected(todayISO()); }} className="ml-auto text-xs font-bold px-3 py-1.5 rounded-full border border-slate-300 hover:bg-slate-50" data-testid="cal-go-today">Hoje</button>
      </div>
      <MonthChips year={ym.y} month={ym.m} onPick={(d) => setYm({ y: d.getFullYear(), m: d.getMonth() })} />
      <Legend />
      <div className="-mx-3 md:mx-0 bg-white md:rounded-xl overflow-hidden">
        <MonthGrid cells={cells} month={ym.m} byDay={byDay} selected={selected} onSelect={setSelected} onOpen={openOcc} />
      </div>

      <section className="bg-white rounded-2xl border border-slate-200 p-3 space-y-2" data-testid="cal-selected-day">
        <div className="flex items-center">
          <h2 className="font-heading text-sm font-black text-[#1e3a8a] capitalize flex-1">{longDate(selected)}</h2>
          <button onClick={() => openNew(selected)} className="text-[12px] font-bold text-[#1a73e8] inline-flex items-center gap-1 px-2 py-1 rounded-full hover:bg-blue-50" data-testid="cal-add-on-day"><Plus className="w-4 h-4" />Adicionar</button>
        </div>
        {dayList.length ? dayList.map((o) => <OccChip key={o.item_id} o={o} onClick={openOcc} />) : <p className="text-[12px] text-slate-400">Nada agendado neste dia.</p>}
      </section>

      {showOverdue && (
        <section ref={overdueRef} className="bg-[#fef7e0] rounded-2xl p-3 space-y-2 scroll-mt-20" data-testid="cal-overdue-list">
          <h2 className="font-heading text-sm font-black text-[#7a5900] flex items-center gap-1.5"><AlertTriangle className="w-4 h-4" />Atrasados — finalize ou reagende</h2>
          {overdue.map((o) => (
            <div key={`${o.item_id}-${o.date}`} className="flex items-center gap-2">
              <span className="text-[11px] text-[#7a5900] w-12 shrink-0 tabular-nums">{o.date.slice(8)}/{o.date.slice(5, 7)}</span>
              <div className="flex-1 min-w-0"><OccChip o={o} onClick={openOcc} /></div>
            </div>
          ))}
        </section>
      )}

      <button onClick={() => openNew(selected)} className="fixed bottom-44 md:bottom-28 right-5 md:right-6 w-14 h-14 rounded-2xl bg-[#d3e3fd] text-[#1a73e8] shadow-xl flex items-center justify-center hover:brightness-95 active:scale-95 transition-[filter,transform] z-30" aria-label="Novo agendamento" data-testid="cal-fab"><Plus className="w-7 h-7" /></button>
      {dialogs}
    </div>
  );
}
